/* eslint-disable */
import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IconButton } from '@mui/material';
import { Delete, Link as LinkIcon, Add, Remove } from '@mui/icons-material';
import { productActions } from '@/Actions';
import { BaseModal } from '../BaseModal';
import { Button } from '@/Components/ui/button';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/Components/ui/select';
import styles from './PackSizeConfigModal.module.scss';

const PackSizeConfigModal = (props) => {
	const {
		open,
		handleClose,
		handleSave,
		productData = {},
		isViewMode = false,
	} = props;

	const dispatch = useDispatch();
	const {
		productName,
		productImage,
		displayName,
		variantIndex,
		existingPackSizes = [],
	} = productData;

	// Get products from Redux state
	const products = useSelector((state) => state.product.products);

	// Filter products with skuType === 'PACKAGING'
	const packagingProducts = useMemo(() => {
		if (!products || typeof products !== 'object') return [];
		const productArray = Array.isArray(products)
			? products
			: Object.values(products);
		return productArray.filter(
			(product) =>
				product?.skuType?.toUpperCase() === 'PACKAGING' &&
				product?.isEnabled !== false
		);
	}, [products]);

	// Box options derived from all packaging products' variants
	const boxOptions = useMemo(() => {
		const allVariants = [];
		packagingProducts.forEach((product) => {
			if (!product?.ProductVariant) return;
			// ProductVariant can be an object like {1: {}, 2: {}} or an array
			const variantArray = Array.isArray(product.ProductVariant)
				? product.ProductVariant
				: Object.values(product.ProductVariant);
			variantArray
				.filter((v) => v?.isDisabled !== true)
				.forEach((variant) => {
					allVariants.push({
						id: String(variant.id),
						name: `${product.productName || product.name} (${
							variant.displayName || `Variant ${variant.id}`
						})`,
					});
				});
		});
		return allVariants;
	}, [packagingProducts]);

	const [packSizes, setPackSizes] = useState([
		{ id: 1, quantity: 0, boxId: '' },
	]);

	// Check for duplicate pack sizes (same quantity > 0)
	const hasDuplicatePackSizes = useMemo(() => {
		const quantities = packSizes
			.filter((pack) => pack.quantity > 0)
			.map((pack) => pack.quantity);
		return new Set(quantities).size !== quantities.length;
	}, [packSizes]);

	// Get duplicate quantity values for highlighting
	const duplicateQuantities = useMemo(() => {
		const quantities = packSizes
			.filter((pack) => pack.quantity > 0)
			.map((pack) => pack.quantity);
		const seen = new Set();
		const duplicates = new Set();
		quantities.forEach((qty) => {
			if (seen.has(qty)) {
				duplicates.add(qty);
			}
			seen.add(qty);
		});
		return duplicates;
	}, [packSizes]);

	// Fetch products on mount if not already loaded
	useEffect(() => {
		if (open && (!products || Object.keys(products).length === 0)) {
			dispatch(productActions.fetchProducts());
		}
	}, [open, products, dispatch]);

	useEffect(() => {
		if (open) {
			// Load existing pack sizes or reset to default
			if (existingPackSizes && existingPackSizes.length > 0) {
				setPackSizes(existingPackSizes);
			} else {
				setPackSizes([{ id: 1, quantity: 0, boxId: '' }]);
			}
		}
	}, [open, existingPackSizes]);

	const handleAddPack = () => {
		const newId =
			packSizes.length > 0
				? Math.max(...packSizes.map((p) => p.id)) + 1
				: 1;
		setPackSizes([...packSizes, { id: newId, quantity: 0, boxId: '' }]);
	};

	const handleRemovePack = (id) => {
		if (packSizes.length > 1) {
			setPackSizes(packSizes.filter((pack) => pack.id !== id));
		}
	};

	const handleQuantityChange = (id, delta) => {
		setPackSizes(
			packSizes.map((pack) => {
				if (pack.id === id) {
					const newQty = Math.max(0, pack.quantity + delta);
					return { ...pack, quantity: newQty };
				}
				return pack;
			})
		);
	};

	const handleQuantityInput = (id, value) => {
		const numValue = parseInt(value, 10) || 0;
		setPackSizes(
			packSizes.map((pack) => {
				if (pack.id === id) {
					return { ...pack, quantity: Math.max(0, numValue) };
				}
				return pack;
			})
		);
	};

	const handleBoxChange = (id, boxId) => {
		setPackSizes(
			packSizes.map((pack) => {
				if (pack.id === id) {
					return { ...pack, boxId };
				}
				return pack;
			})
		);
	};

	const handleConfirm = () => {
		handleSave?.({
			variantIndex,
			packSizes: packSizes.filter((pack) => pack.quantity > 0),
		});
		handleClose();
	};

	const renderFooterBtns = () => (
		<div
			style={{
				display: 'flex',
				justifyContent: 'flex-end',
				width: '100%',
			}}
		>
			<div
				style={{
					display: 'flex',
					gap: '1rem',
					width: '30%',
					alignItems: 'right',
				}}
			>
				<Button
					className='flex-1'
					variant='outline'
					onClick={handleClose}
					style={{
						backgroundColor: 'white',
						border: '1px solid #e0e0e0',
						color: '#424242',
					}}
				>
					Cancel
				</Button>
				<Button
					className='flex-1'
					onClick={handleConfirm}
					disabled={isViewMode || hasDuplicatePackSizes}
					style={{
						backgroundColor:
							isViewMode || hasDuplicatePackSizes
								? '#e0e0e0'
								: '#f5b800',
						border: 'none',
						color:
							isViewMode || hasDuplicatePackSizes
								? '#9e9e9e'
								: '#333',
						cursor:
							isViewMode || hasDuplicatePackSizes
								? 'not-allowed'
								: 'pointer',
					}}
				>
					Save
				</Button>
			</div>
		</div>
	);

	return (
		<BaseModal
			open={open}
			handleClose={handleClose}
			handleConfirm={handleConfirm}
			width='40rem'
			height='auto'
			showClose={true}
			hideFooter={false}
			renderBtns={renderFooterBtns}
			aria={{
				label: 'pack-size-config-modal',
				description: 'Configure pack sizes for the product',
			}}
			customStyles={{
				minHeight: '400px',
				maxHeight: '90vh',
			}}
		>
			<div className={styles.root}>
				{/* Product Info Header */}
				<div className={styles.productInfo}>
					<div className={styles.productImage}>
						{productImage ? (
							<img src={productImage} alt={productName} />
						) : (
							<div className={styles.placeholderImage}>
								<span>No Image</span>
							</div>
						)}
					</div>
					<div className={styles.productDetails}>
						<h3 className={styles.productName}>
							{productName || 'Product Name'}
						</h3>
						{displayName && (
							<span className={styles.variantTag}>
								{displayName}
							</span>
						)}
					</div>
				</div>

				{/* View Mode Banner */}
				{isViewMode && (
					<div className={styles.viewModeBanner}>
						<span className={styles.viewModeIcon}>👁</span>
						<span>
							You are viewing in read-only mode. To make changes,
							please edit the product.
						</span>
					</div>
				)}

				{/* Pack Size Configuration Section */}
				<div className={styles.configSection}>
					<div className={styles.configHeader}>
						<div>
							<h4 className={styles.configTitle}>
								Pack Size Configuration
							</h4>
							<p className={styles.configSubtitle}>
								Add pack sizes and map each pack to a box
							</p>
						</div>
						{!isViewMode && (
							<Button
								variant='outline'
								className={styles.addPackBtn}
								onClick={handleAddPack}
							>
								+ Add Packs
							</Button>
						)}
					</div>

					{/* Column Headers */}
					<div className={styles.columnHeaders}>
						<span className={styles.packSizeHeader}>
							Pack Size (in pcs)
						</span>
						<span className={styles.boxHeader}>Configure Box</span>
						<span className={styles.actionHeader}></span>
					</div>

					{/* Pack Size Rows */}
					<div className={styles.packSizeRows}>
						{packSizes.map((pack) => {
							const isDuplicate =
								pack.quantity > 0 &&
								duplicateQuantities.has(pack.quantity);
							return (
								<div
									key={pack.id}
									className={`${styles.packSizeRow} ${
										isDuplicate ? styles.duplicateRow : ''
									}`}
								>
									<div className={styles.packSizeInput}>
										<span className={styles.packLabel}>
											Pack of
										</span>
										<div className={styles.quantityControl}>
											<button
												className={styles.quantityBtn}
												onClick={() =>
													handleQuantityChange(
														pack.id,
														-1
													)
												}
												type='button'
												disabled={isViewMode}
											>
												<Remove fontSize='small' />
											</button>
											<input
												type='number'
												value={pack.quantity}
												onChange={(e) =>
													handleQuantityInput(
														pack.id,
														e.target.value
													)
												}
												className={`${
													styles.quantityInput
												} ${
													isDuplicate
														? styles.duplicateInput
														: ''
												}`}
												min={0}
												disabled={isViewMode}
											/>
											<button
												className={styles.quantityBtn}
												onClick={() =>
													handleQuantityChange(
														pack.id,
														1
													)
												}
												type='button'
												disabled={isViewMode}
											>
												<Add fontSize='small' />
											</button>
										</div>
										<LinkIcon
											className={styles.linkIcon}
											fontSize='small'
										/>
									</div>

									<div className={styles.boxSelect}>
										<Select
											value={pack.boxId}
											onValueChange={(value) =>
												handleBoxChange(pack.id, value)
											}
											disabled={isViewMode}
										>
											<SelectTrigger
												className={styles.selectTrigger}
												disabled={isViewMode}
											>
												<SelectValue placeholder='Select Box' />
											</SelectTrigger>
											<SelectContent className='z-[9999] outline-none'>
												{boxOptions.map((box) => {
													// Check if this variant is already selected in another pack
													const isAlreadySelected =
														packSizes.some(
															(p) =>
																p.id !==
																	pack.id &&
																p.boxId ===
																	box.id
														);
													return (
														<SelectItem
															key={box.id}
															value={box.id}
															disabled={
																isAlreadySelected
															}
															className={
																isAlreadySelected
																	? 'opacity-50 cursor-not-allowed'
																	: ''
															}
														>
															{box.name}
															{isAlreadySelected
																? ' (Already Selected)'
																: ''}
														</SelectItem>
													);
												})}
											</SelectContent>
										</Select>
									</div>

									<div className={styles.deleteAction}>
										<IconButton
											onClick={() =>
												handleRemovePack(pack.id)
											}
											disabled={
												packSizes.length === 1 ||
												isViewMode
											}
											size='small'
											className={styles.deleteBtn}
										>
											<Delete
												fontSize='small'
												sx={{
													color:
														packSizes.length ===
															1 || isViewMode
															? '#ccc'
															: '#d32f2f',
												}}
											/>
										</IconButton>
									</div>
								</div>
							);
						})}
					</div>

					{/* Duplicate Pack Size Error Message */}
					{hasDuplicatePackSizes && (
						<div className={styles.errorMessage}>
							<span>
								⚠️ Pack sizes must be unique. Please remove or
								change the duplicate pack sizes.
							</span>
						</div>
					)}
				</div>
			</div>
		</BaseModal>
	);
};

export default PackSizeConfigModal;
